import type {
  CustomViewWidget,
  CustomViewWidgetDefinition,
  CustomViewWidgetType,
} from '../../core/types/data-models';
import type {
  FlatViewAnalyticsEntities,
  FlatViewAnalyticsRow,
} from '../../core/services/resources';

/** The cross-filtered dimensions of the custom-view filter bar. */
export interface CustomViewSelection {
  reportInstanceIds: number[];
  geoLocationIds: number[];
  aiModelIds: number[];
  categoryIds: number[];
  personaIds: number[];
  stageIds: number[];
  intentionTypes: string[];
}

export const EMPTY_SELECTION: CustomViewSelection = {
  reportInstanceIds: [],
  geoLocationIds: [],
  aiModelIds: [],
  categoryIds: [],
  personaIds: [],
  stageIds: [],
  intentionTypes: [],
};

/**
 * View-filter dimension → flat-view column, per data source. The two views
 * name the same prompt-level dimensions differently (the commerce view
 * prefixes them with `reportInstancePrompt`). `reportInstanceIds` is not
 * here — it maps to the shared `filters.reportInstanceIds` scope on both.
 */
const FILTER_FIELD_BY_SOURCE: Record<
  string,
  Partial<Record<keyof CustomViewSelection, string>>
> = {
  'report-instance-brand-vw': {
    geoLocationIds: 'geoLocationId',
    aiModelIds: 'aiModelId',
    categoryIds: 'categoryId',
    personaIds: 'personaId',
    stageIds: 'stageId',
    intentionTypes: 'intentionType',
  },
  'report-instance-commerce-item-vw': {
    geoLocationIds: 'reportInstancePromptGeoLocationId',
    aiModelIds: 'reportInstancePromptAiModelId',
    categoryIds: 'reportInstancePromptCategoryId',
    personaIds: 'reportInstancePromptPersonaId',
    stageIds: 'reportInstancePromptFunnelStageId',
    intentionTypes: 'reportInstancePromptIntentionType',
  },
};

interface RequestFilters {
  reportInstanceIds?: number[];
  additional?: Array<{ field: string; values: Array<string | number> }>;
  [key: string]: unknown;
}

/**
 * Top-level keys the analytics endpoint accepts (mirrors the engine's request
 * allowlist). The endpoint rejects unknown keys, and requests generated before
 * the generator's output schema was closed can carry stray siblings such as
 * `series` — drop those instead of failing the whole widget.
 */
const ANALYTICS_REQUEST_KEYS = [
  'filters',
  'groupBy',
  'perUnit',
  'aggregations',
  'windows',
  'having',
  'orderBy',
  'limit',
  'page',
  'pageSize',
  'enrich',
];

/**
 * Merge the view-level applied filters into a widget's stored analytics
 * request: applied report instances override the request's scope, every
 * other non-empty dimension is appended to `filters.additional` using the
 * data source's column names.
 */
export function mergeWidgetRequest(
  widget: CustomViewWidget,
  selection: CustomViewSelection,
): Record<string, unknown> {
  const request = unwrapRequest(widget.contentDefinition.request ?? {});
  const filters: RequestFilters = { ...(request.filters as RequestFilters | undefined) };
  if (selection.reportInstanceIds.length > 0) {
    filters.reportInstanceIds = selection.reportInstanceIds;
  }
  const fieldMap = FILTER_FIELD_BY_SOURCE[widget.dataSource] ?? {};
  const additional = [...(filters.additional ?? [])];
  for (const [dimension, field] of Object.entries(fieldMap)) {
    const values = selection[dimension as keyof CustomViewSelection];
    if (values.length > 0) additional.push({ field, values });
  }
  if (additional.length > 0) filters.additional = additional;
  const known = Object.entries(request).filter(([key]) => ANALYTICS_REQUEST_KEYS.includes(key));
  return { ...Object.fromEntries(known), filters };
}

/**
 * Older LLM-generated widgets may carry the model's whole answer nested
 * inside `request` (`{ request: { request, display } }`) — unwrap so
 * those rows still render. Newly generated requests are already flat.
 */
export function unwrapRequest(request: Record<string, unknown>): Record<string, unknown> {
  const inner = request.request;
  if (
    !Array.isArray(request.aggregations) &&
    inner &&
    typeof inner === 'object' &&
    !Array.isArray(inner)
  ) {
    return inner as Record<string, unknown>;
  }
  return request;
}

/** Sensible starter request per data source so a new widget renders data. */
export function defaultRequestFor(dataSource: string): Record<string, unknown> {
  const groupBy =
    dataSource === 'report-instance-commerce-item-vw' ? 'commerceMerchantId' : 'aiModelId';
  return {
    groupBy: [groupBy],
    aggregations: [{ fn: 'count', field: 'id', alias: 'mentions' }],
    orderBy: [{ field: 'mentions', direction: 'desc' }],
    limit: 10,
    enrich: true,
  };
}

export const WIDGET_TYPES: CustomViewWidgetType[] = ['bar', 'line', 'pie', 'table', 'markdown'];

/** The widget grid: 4 columns; each row unit is 360px tall with a 16px gap. */
export const GRID_COLUMNS = 4;
export const GRID_ROW_HEIGHT = 360;
export const GRID_GAP_PX = 16;

/** Clamp a stored widget width/height to the grid's 1–4 span range. */
export function clampSpan(span: number): number {
  return Math.min(GRID_COLUMNS, Math.max(1, Math.round(span) || 1));
}

/**
 * Pixel height of a widget's content area for a given row span: the
 * spanned grid rows (plus the gaps between them) minus the card's
 * header + padding.
 */
export function widgetContentHeight(rows: number): number {
  const span = clampSpan(rows);
  return span * GRID_ROW_HEIGHT + (span - 1) * GRID_GAP_PX - 96;
}

/**
 * Display label for a row's group value: the enriched entity name when
 * available (entity map keys are strings), else the raw value.
 */
export function rowLabel(
  row: FlatViewAnalyticsRow,
  groupField: string | undefined,
  entities: FlatViewAnalyticsEntities | undefined,
): string {
  if (!groupField) return '—';
  const value = row[groupField];
  if (value == null) return '—';
  const entity = entities?.[groupField]?.[String(value)];
  const name = entity?.name;
  return typeof name === 'string' && name ? name : String(value);
}

/** Numeric aggregation keys of a result set: keys not in groupBy with numeric values. */
export function numericSeriesKeys(
  rows: FlatViewAnalyticsRow[],
  groupBy: string[],
): string[] {
  const first = rows[0];
  if (!first) return [];
  return Object.keys(first).filter(
    (key) => !groupBy.includes(key) && typeof first[key] === 'number',
  );
}

/** One chart-ready series: values aligned with the result rows. */
export interface ResolvedWidgetSeries {
  label: string;
  format: 'percent' | 'number';
  values: number[];
}

/**
 * Resolve what the widget actually plots. With declared `series` specs
 * (ratios/values over aggregation aliases — the analytics API cannot
 * compute ratios itself), only those are plotted; otherwise every numeric
 * aggregation key becomes a plain series, as before.
 */
export function resolveWidgetSeries(
  def: CustomViewWidgetDefinition,
  rows: FlatViewAnalyticsRow[],
  seriesKeys: string[],
): ResolvedWidgetSeries[] {
  const specs = (def.series ?? []).filter((s) =>
    s.type === 'ratio'
      ? typeof s.numerator === 'string' && typeof s.denominator === 'string'
      : s.type === 'value' && typeof s.alias === 'string',
  );
  if (specs.length === 0) {
    return seriesKeys.map((key) => ({
      label: key,
      format: 'number',
      values: rows.map((r) => Number(r[key] ?? 0)),
    }));
  }
  return specs.map((s) => {
    const format = s.format ?? (s.type === 'ratio' ? 'percent' : 'number');
    const values = rows.map((r) => {
      if (s.type === 'value') return Number(r[s.alias as string] ?? 0);
      const numerator = Number(r[s.numerator as string] ?? 0);
      const denominator = Number(r[s.denominator as string] ?? 0);
      const ratio = denominator > 0 ? numerator / denominator : 0;
      return format === 'percent' ? ratio * 100 : ratio;
    });
    const label =
      s.label ?? (s.type === 'value' ? (s.alias as string) : `${s.numerator}/${s.denominator}`);
    return { label, format, values };
  });
}

/** Render one series value for tooltips / the summary list. */
export function formatSeriesValue(
  value: number | null | undefined,
  format: 'percent' | 'number',
): string {
  if (value == null || Number.isNaN(value)) return '—';
  if (format === 'percent') return `${value.toFixed(1)}%`;
  return String(Math.round(value * 100) / 100);
}
